import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Drawer,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../theme';

const API_BASE = 'https://filtrd-prod-f09ee9687166.herokuapp.com/api';
const STORAGE_KEY = 'admin_api_key';

interface Category {
  id: string;
  name: string;
  slug: string;
  description?: string;
  createdAt?: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  categories?: Category[];
}

const AdminCategoriesPage: React.FC = () => {
  const navigate = useNavigate();
  const [apiKey, setApiKey] = useState(() => localStorage.getItem(STORAGE_KEY) || '');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Drawer state
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Map of categoryId -> list of user names
  const [categoryUsersMap, setCategoryUsersMap] = useState<Record<string, string[]>>({});

  // Edit state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editCategory, setEditCategory] = useState<Category | null>(null);
  const [editName, setEditName] = useState('');
  const [editSlug, setEditSlug] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteCategory, setDeleteCategory] = useState<Category | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchCategories = useCallback(async () => {
    if (!apiKey) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch categories
      const categoriesResponse = await fetch(`${API_BASE}/categories?limit=100`, {
        headers: {
          'X-API-Key': apiKey,
          'X-User-Id': 'admin',
          'X-App-Name': 'Filtrd',
          'X-Permissions': 'categories:read',
        },
      });

      if (!categoriesResponse.ok) {
        if (categoriesResponse.status === 401 || categoriesResponse.status === 403) {
          throw new Error('Invalid API key');
        }
        throw new Error(`Failed to fetch: ${categoriesResponse.statusText}`);
      }

      const categoriesData = await categoriesResponse.json();
      const categoryItems = categoriesData?.data?.items || categoriesData?.items || categoriesData?.data || [];
      setCategories(Array.isArray(categoryItems) ? categoryItems : []);

      // Fetch users with categories
      const usersResponse = await fetch(`${API_BASE}/users?limit=100&include=categories`, {
        headers: {
          'X-API-Key': apiKey,
          'X-User-Id': 'admin',
          'X-App-Name': 'Filtrd',
          'X-Permissions': 'users:read',
        },
      });

      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        const users = usersData?.data?.items || [];

        // Build map of categoryId -> list of user names
        const usersMap: Record<string, string[]> = {};
        users.forEach((user: User) => {
          if (user.categories && Array.isArray(user.categories)) {
            user.categories.forEach((category: Category) => {
              if (!usersMap[category.id]) {
                usersMap[category.id] = [];
              }
              usersMap[category.id].push(user.name);
            });
          }
        });
        setCategoryUsersMap(usersMap);
      }

      setIsAuthenticated(true);
      localStorage.setItem(STORAGE_KEY, apiKey);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch categories');
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, [apiKey]);

  const handleCategoryClick = (category: Category) => {
    setSelectedCategory(category);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedCategory(null);
  };

  const handleEditOpen = (category: Category, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setEditCategory(category);
    setEditName(category.name);
    setEditSlug(category.slug);
    setEditDescription(category.description || '');
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditCategory(null);
  };

  const handleEditSave = async () => {
    if (!editCategory || !apiKey) return;
    setSaving(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/categories/${editCategory.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey,
          'X-User-Id': 'admin',
          'X-App-Name': 'Filtrd',
          'X-Permissions': 'categories:write',
        },
        body: JSON.stringify({
          name: editName,
          slug: editSlug,
          description: editDescription || undefined,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.message || `Failed to update: ${response.statusText}`);
      }

      handleEditClose();
      // Update local state and drawer if open
      const updated = { ...editCategory, name: editName, slug: editSlug, description: editDescription };
      setCategories((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
      if (selectedCategory?.id === updated.id) {
        setSelectedCategory(updated);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update category');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOpen = (category: Category, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setDeleteCategory(category);
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setDeleteCategory(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteCategory || !apiKey) return;
    setDeleting(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/categories/${deleteCategory.id}`, {
        method: 'DELETE',
        headers: {
          'X-API-Key': apiKey,
          'X-User-Id': 'admin',
          'X-App-Name': 'Filtrd',
          'X-Permissions': 'categories:delete',
        },
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.message || `Failed to delete: ${response.statusText}`);
      }

      // Close drawer if this category was selected
      if (selectedCategory?.id === deleteCategory.id) {
        handleCloseDrawer();
      }
      // Remove from local state
      setCategories((prev) => prev.filter((c) => c.id !== deleteCategory.id));
      setCategoryUsersMap((prev) => {
        const next = { ...prev };
        delete next[deleteCategory.id];
        return next;
      });
      handleDeleteClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete category');
    } finally {
      setDeleting(false);
    }
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    fetchCategories();
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCategories([]);
    localStorage.removeItem(STORAGE_KEY);
    setApiKey('');
  };

  useEffect(() => {
    if (apiKey && !isAuthenticated) {
      fetchCategories();
    }
  }, [apiKey, isAuthenticated, fetchCategories]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: colors.deepNavy,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Container maxWidth="sm">
          <Paper
            sx={{
              p: 4,
              backgroundColor: colors.whiteAlpha[10],
              border: colors.borders.whiteAlpha10,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color: colors.pureWhite,
                mb: 3,
                fontWeight: 700,
                textAlign: 'center',
              }}
            >
              Categories Admin
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleLogin}>
              <TextField
                fullWidth
                label="API Key"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    color: colors.pureWhite,
                    '& fieldset': {
                      borderColor: colors.whiteAlpha[20],
                    },
                    '&:hover fieldset': {
                      borderColor: colors.whiteAlpha[30],
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: colors.signalOrange,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: colors.slate[400],
                    '&.Mui-focused': {
                      color: colors.signalOrange,
                    },
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || !apiKey}
                sx={{
                  backgroundColor: colors.signalOrange,
                  '&:hover': {
                    backgroundColor: colors.signalOrangeHover,
                  },
                  '&:disabled': {
                    backgroundColor: colors.slate[600],
                  },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: colors.deepNavy,
        py: 4,
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => navigate('/admin')}
              sx={{ color: colors.pureWhite }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              variant="h4"
              sx={{
                color: colors.pureWhite,
                fontWeight: 700,
              }}
            >
              Categories Admin
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={() => navigate('/admin')}
              sx={{
                color: colors.signalOrange,
                borderColor: colors.signalOrange,
                '&:hover': {
                  borderColor: colors.signalOrangeHover,
                  backgroundColor: colors.whiteAlpha[10],
                },
              }}
              variant="outlined"
            >
              Onboardings
            </Button>
            <Button
              onClick={() => navigate('/admin/users')}
              sx={{
                color: colors.signalOrange,
                borderColor: colors.signalOrange,
                '&:hover': {
                  borderColor: colors.signalOrangeHover,
                  backgroundColor: colors.whiteAlpha[10],
                },
              }}
              variant="outlined"
            >
              Users
            </Button>
            <Button
              onClick={() => navigate('/admin/x-users')}
              sx={{
                color: colors.signalOrange,
                borderColor: colors.signalOrange,
                '&:hover': {
                  borderColor: colors.signalOrangeHover,
                  backgroundColor: colors.whiteAlpha[10],
                },
              }}
              variant="outlined"
            >
              X Users
            </Button>
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchCategories}
              disabled={loading}
              sx={{
                color: colors.pureWhite,
                borderColor: colors.whiteAlpha[30],
                '&:hover': {
                  borderColor: colors.pureWhite,
                  backgroundColor: colors.whiteAlpha[10],
                },
              }}
              variant="outlined"
            >
              Refresh
            </Button>
            <Button
              onClick={handleLogout}
              sx={{
                color: colors.error,
                borderColor: colors.error,
                '&:hover': {
                  backgroundColor: colors.whiteAlpha[10],
                },
              }}
              variant="outlined"
            >
              Logout
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Stats */}
        <Typography sx={{ mb: 3, color: colors.slate[400] }}>
          Total: {categories.length} categories
        </Typography>

        {/* Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: colors.signalOrange }} />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              backgroundColor: colors.whiteAlpha[10],
              border: colors.borders.whiteAlpha10,
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Slug</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Description</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Users</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Created</TableCell>
                  <TableCell sx={{ color: colors.slate[400], fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.map((category) => (
                  <TableRow
                    key={category.id}
                    onClick={() => handleCategoryClick(category)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: colors.whiteAlpha[10],
                      },
                    }}
                  >
                    <TableCell sx={{ color: colors.pureWhite, fontWeight: 500 }}>
                      {category.name}
                    </TableCell>
                    <TableCell sx={{ color: colors.signalOrange }}>
                      {category.slug}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: colors.slate[400],
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {category.description || '-'}
                    </TableCell>
                    <TableCell sx={{ color: colors.slate[400] }}>
                      {categoryUsersMap[category.id]?.length > 0
                        ? `${categoryUsersMap[category.id].length} users`
                        : '0'}
                    </TableCell>
                    <TableCell sx={{ color: colors.slate[400], fontSize: '0.75rem' }}>
                      {formatDate(category.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleEditOpen(category, e)}
                          sx={{
                            color: colors.signalOrange,
                            '&:hover': { backgroundColor: colors.whiteAlpha[10] },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => handleDeleteOpen(category, e)}
                          sx={{
                            color: colors.error,
                            '&:hover': { backgroundColor: colors.whiteAlpha[10] },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {categories.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ color: colors.slate[400], textAlign: 'center', py: 4 }}>
                      No categories found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>

      {/* Category Detail Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 500 },
            backgroundColor: colors.deepNavy,
            borderLeft: colors.borders.whiteAlpha10,
          },
        }}
      >
        {selectedCategory && (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Drawer Header */}
            <Box
              sx={{
                p: 3,
                borderBottom: colors.borders.whiteAlpha10,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <Box>
                <Typography variant="h5" sx={{ color: colors.pureWhite, fontWeight: 700, mb: 1 }}>
                  {selectedCategory.name}
                </Typography>
                <Typography sx={{ color: colors.signalOrange, fontSize: '0.875rem', mb: 1 }}>
                  /{selectedCategory.slug}
                </Typography>
                {selectedCategory.description && (
                  <Box sx={{ mt: 2, p: 2, backgroundColor: colors.whiteAlpha[10], borderRadius: 1 }}>
                    <Typography sx={{ color: colors.slate[400], fontSize: '0.75rem', mb: 0.5, textTransform: 'uppercase', letterSpacing: 1 }}>
                      Description
                    </Typography>
                    <Typography sx={{ color: colors.pureWhite, fontSize: '0.875rem', lineHeight: 1.6 }}>
                      {selectedCategory.description}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton
                  onClick={() => handleEditOpen(selectedCategory)}
                  sx={{ color: colors.signalOrange }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  onClick={() => handleDeleteOpen(selectedCategory)}
                  sx={{ color: colors.error }}
                >
                  <DeleteIcon />
                </IconButton>
                <IconButton onClick={handleCloseDrawer} sx={{ color: colors.slate[400] }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Users List */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
              <Typography variant="h6" sx={{ color: colors.pureWhite, mb: 2 }}>
                Users ({categoryUsersMap[selectedCategory.id]?.length || 0})
              </Typography>

              {categoryUsersMap[selectedCategory.id] && categoryUsersMap[selectedCategory.id].length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {categoryUsersMap[selectedCategory.id].map((userName, idx) => (
                    <Chip
                      key={idx}
                      label={userName}
                      sx={{
                        backgroundColor: colors.whiteAlpha[10],
                        color: colors.pureWhite,
                        border: colors.borders.whiteAlpha10,
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography sx={{ color: colors.slate[400], textAlign: 'center', py: 4 }}>
                  No users in this category
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Drawer>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: colors.deepNavy,
            border: colors.borders.whiteAlpha10,
          },
        }}
      >
        <DialogTitle sx={{ color: colors.pureWhite }}>Edit Category</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            sx={{
              mt: 1,
              mb: 2,
              '& .MuiOutlinedInput-root': {
                color: colors.pureWhite,
                '& fieldset': { borderColor: colors.whiteAlpha[20] },
                '&:hover fieldset': { borderColor: colors.whiteAlpha[30] },
                '&.Mui-focused fieldset': { borderColor: colors.signalOrange },
              },
              '& .MuiInputLabel-root': {
                color: colors.slate[400],
                '&.Mui-focused': { color: colors.signalOrange },
              },
            }}
          />
          <TextField
            fullWidth
            label="Slug"
            value={editSlug}
            onChange={(e) => setEditSlug(e.target.value)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                color: colors.pureWhite,
                '& fieldset': { borderColor: colors.whiteAlpha[20] },
                '&:hover fieldset': { borderColor: colors.whiteAlpha[30] },
                '&.Mui-focused fieldset': { borderColor: colors.signalOrange },
              },
              '& .MuiInputLabel-root': {
                color: colors.slate[400],
                '&.Mui-focused': { color: colors.signalOrange },
              },
            }}
          />
          <TextField
            fullWidth
            label="Description"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            multiline
            rows={3}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: colors.pureWhite,
                '& fieldset': { borderColor: colors.whiteAlpha[20] },
                '&:hover fieldset': { borderColor: colors.whiteAlpha[30] },
                '&.Mui-focused fieldset': { borderColor: colors.signalOrange },
              },
              '& .MuiInputLabel-root': {
                color: colors.slate[400],
                '&.Mui-focused': { color: colors.signalOrange },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleEditClose} sx={{ color: colors.slate[400] }}>
            Cancel
          </Button>
          <Button
            onClick={handleEditSave}
            disabled={saving || !editName.trim() || !editSlug.trim()}
            variant="contained"
            sx={{
              backgroundColor: colors.signalOrange,
              '&:hover': { backgroundColor: colors.signalOrangeHover },
              '&:disabled': { backgroundColor: colors.slate[600] },
            }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteClose}
        PaperProps={{
          sx: {
            backgroundColor: colors.deepNavy,
            border: colors.borders.whiteAlpha10,
          },
        }}
      >
        <DialogTitle sx={{ color: colors.pureWhite }}>Delete Category</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: colors.slate[400], mb: 1 }}>
            Are you sure you want to delete <strong style={{ color: colors.pureWhite }}>{deleteCategory?.name}</strong>?
          </Typography>
          <Typography sx={{ color: colors.error, fontSize: '0.875rem' }}>
            This will also remove all user-category associations
            {(categoryUsersMap[deleteCategory?.id || '']?.length || 0) > 0 &&
              ` for ${categoryUsersMap[deleteCategory?.id || ''].length} user(s)`}
            .
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteClose} sx={{ color: colors.slate[400] }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={deleting}
            variant="contained"
            sx={{
              backgroundColor: colors.error,
              '&:hover': { backgroundColor: '#dc2626' },
              '&:disabled': { backgroundColor: colors.slate[600] },
            }}
          >
            {deleting ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminCategoriesPage;
